import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Save, Sparkles, BookOpen, Info, Award, Upload, Image as ImageIcon, X } from "lucide-react";
import api from "../../lib/api";
import ThemedSelect from "../../components/ThemedSelect";
import TtsVoiceSettings from "../../components/TtsVoiceSettings";
import OnboardingAudioSettings from "../../components/OnboardingAudioSettings";

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [modules, setModules] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        welcome_video_url: "",
        welcome_title: "",
        welcome_description: "",
        required_module_id: "",
    });

    // Certificate settings (iter-20)
    const [certSettings, setCertSettings] = useState(null);
    const [certForm, setCertForm] = useState({
        signatory_name: "",
        signatory_title: "",
        signature_image_url: "",
        signature_mode: "electronic",
        logo_url: "",
        show_logo: true,
        show_signature: true,
    });
    const [savingCert, setSavingCert] = useState(false);
    const [uploadingCert, setUploadingCert] = useState(null); // 'logo' | 'signature' | null

    useEffect(() => {
        Promise.all([
            api.get("/onboarding/settings"),
            api.get("/modules"),
            api.get("/onboarding/certificate-settings"),
        ]).then(([s, m, cs]) => {
            setSettings(s.data);
            setForm({
                welcome_video_url: s.data.welcome_video_url || "",
                welcome_title: s.data.welcome_title || "",
                welcome_description: s.data.welcome_description || "",
                required_module_id: s.data.required_module_id || "",
            });
            setModules(m.data);
            setCertSettings(cs.data);
            setCertForm({
                signatory_name: cs.data.signatory_name || "",
                signatory_title: cs.data.signatory_title || "",
                signature_image_url: cs.data.signature_image_url || "",
                signature_mode: cs.data.signature_mode || "electronic",
                logo_url: cs.data.logo_url || "",
                show_logo: !!cs.data.show_logo,
                show_signature: !!cs.data.show_signature,
            });
        });
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/onboarding/settings", form);
            setSettings(data);
            toast.success("Paramètres enregistrés");
        } catch {
            toast.error("Erreur");
        } finally {
            setSaving(false);
        }
    };

    const saveCert = async () => {
        setSavingCert(true);
        try {
            const { data } = await api.put(
                "/admin/onboarding/certificate-settings",
                certForm,
            );
            setCertSettings(data);
            setCertForm({
                signatory_name: data.signatory_name || "",
                signatory_title: data.signatory_title || "",
                signature_image_url: data.signature_image_url || "",
                signature_mode: data.signature_mode || "electronic",
                logo_url: data.logo_url || "",
                show_logo: !!data.show_logo,
                show_signature: !!data.show_signature,
            });
            toast.success("Certificat mis à jour");
        } catch {
            toast.error("Erreur");
        } finally {
            setSavingCert(false);
        }
    };

    const uploadCertImage = async (file, kind) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image trop lourde (max 5 Mo)");
            return;
        }
        setUploadingCert(kind);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = data.url;
            setCertForm((f) =>
                kind === "logo" ? { ...f, logo_url: url } : { ...f, signature_image_url: url },
            );
            toast.success(kind === "logo" ? "Logo uploadé" : "Signature uploadée");
        } catch {
            toast.error("Upload impossible");
        } finally {
            setUploadingCert(null);
        }
    };

    const downloadPreview = () => {
        // Iter-27.6: dedicated admin preview endpoint that doesn't require
        // the admin to have completed every module first.
        window.open(
            `${api.defaults.baseURL}/admin/onboarding/certificate-preview`,
            "_blank",
            "noopener",
        );
    };

    if (!settings || !certSettings) {
        return <div className="h-32 glass-card rounded-3xl animate-pulse" />;
    }

    const previewUrl = (() => {
        const url = form.welcome_video_url;
        if (!url) return "";
        if (url.includes("youtube.com/embed")) return url;
        const m = url.match(/(?:youtube\.com.*v=|youtu\.be\/)([^&?]+)/);
        if (m) return `https://www.youtube.com/embed/${m[1]}`;
        return url;
    })();

    return (
        <div className="space-y-5">
            <div>
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Configuration de l'expérience d'arrivée
                </p>
                <h2 className="text-2xl font-extrabold text-white">
                    Onboarding & vidéo de bienvenue
                </h2>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 lg:p-6 space-y-4"
                data-testid="admin-onboarding-settings"
            >
                <div className="flex items-start gap-2 p-3 rounded-2xl bg-shariti-orange/5 border border-shariti-orange/20">
                    <Info className="w-4 h-4 text-shariti-orange-light shrink-0 mt-0.5" />
                    <p className="text-xs text-white/70">
                        Tout nouveau apprenant doit valider cet onboarding avant
                        d'accéder à la plateforme. Le module sélectionné ci-dessous est
                        <span className="font-bold text-shariti-orange-light"> obligatoire </span>
                        (toutes les leçons + quiz à 70% minimum).
                    </p>
                </div>

                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" /> URL de la vidéo de bienvenue (YouTube ou Vimeo)
                    </label>
                    <input
                        type="text"
                        placeholder="https://www.youtube.com/embed/VIDEO_ID  ou https://www.youtube.com/watch?v=VIDEO_ID"
                        value={form.welcome_video_url}
                        onChange={(e) =>
                            setForm({ ...form, welcome_video_url: e.target.value })
                        }
                        data-testid="settings-welcome-url"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                    />
                    {/* Iter-27.38 — Indicateur du fallback "hero card premium" */}
                    <div className={`mt-2 px-3 py-2 rounded-xl text-[11px] font-light leading-snug border ${
                        form.welcome_video_url
                            ? "bg-shariti-orange/8 border-shariti-orange/25 text-shariti-orange-light"
                            : "bg-emerald-500/8 border-emerald-500/25 text-emerald-200"
                    }`}>
                        {form.welcome_video_url ? (
                            <>
                                <strong className="font-extrabold">Mode vidéo actif</strong> — la vidéo sera affichée à l'étape "Bienvenue" de l'onboarding. Vide ce champ pour basculer sur le fallback "hero card premium" en attendant ta vraie vidéo.
                            </>
                        ) : (
                            <>
                                <strong className="font-extrabold">Fallback hero card actif</strong> — sans vidéo configurée, l'onboarding affiche automatiquement une carte animée premium (gradient orange, icône Sparkles, accroche "Bienvenue à bord"). Idéal pour démarrer en attendant tes vraies vidéos.
                            </>
                        )}
                    </div>
                </div>

                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                        Titre affiché sur l'étape vidéo
                    </label>
                    <input
                        type="text"
                        value={form.welcome_title}
                        onChange={(e) => setForm({ ...form, welcome_title: e.target.value })}
                        data-testid="settings-welcome-title"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                    />
                </div>

                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                        Description sous la vidéo
                    </label>
                    <textarea
                        rows={3}
                        value={form.welcome_description}
                        onChange={(e) =>
                            setForm({ ...form, welcome_description: e.target.value })
                        }
                        data-testid="settings-welcome-description"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 resize-none"
                    />
                </div>

                <div>
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" /> Module obligatoire de l'onboarding
                    </label>
                    <ThemedSelect
                        options={modules.map((m) => ({
                            value: m.module_id,
                            label: `${m.title} · ${m.level}`,
                        }))}
                        value={form.required_module_id}
                        onChange={(v) => setForm({ ...form, required_module_id: v })}
                        placeholder="Choisir un module"
                        dataTestid="settings-required-module"
                    />
                </div>

                {previewUrl && (
                    <div>
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-2 block">
                            Aperçu
                        </label>
                        <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 max-w-md">
                            <iframe
                                src={previewUrl}
                                title="preview"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                            />
                        </div>
                    </div>
                )}

                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="settings-save"
                    className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>
            </motion.div>

            {/* Iter-27.40 — Voix du formateur Shariti (ElevenLabs) */}
            <div className="pt-4">
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Voix française premium pour les leçons
                </p>
                <h2 className="text-2xl font-extrabold text-white">
                    Voix du formateur
                </h2>
            </div>
            <TtsVoiceSettings />

            {/* Iter-27.49 — Customisation du message audio de bienvenue (onboarding) */}
            <div className="pt-4">
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Personnalisation du message audio d'onboarding
                </p>
                <h2 className="text-2xl font-extrabold text-white">
                    Message de bienvenue
                </h2>
            </div>
            <OnboardingAudioSettings />

            {/* Certificate settings (iter-20) */}
            <div className="pt-4">
                <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-1">
                    Personnalisation du certificat PDF
                </p>
                <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
                    <Award className="w-6 h-6 text-shariti-orange-light" />
                    Certificat de réussite
                </h2>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-3xl p-5 lg:p-6 space-y-5"
                data-testid="admin-certificate-settings"
            >
                <div className="flex items-start gap-2 p-3 rounded-2xl bg-shariti-orange/5 border border-shariti-orange/20">
                    <Info className="w-4 h-4 text-shariti-orange-light shrink-0 mt-0.5" />
                    <p className="text-xs text-white/70">
                        Ces informations apparaissent sur le certificat PDF téléchargé par l'apprenant
                        une fois les 5 modules validés. Customise le logo, le signataire et son titre.
                    </p>
                </div>

                {/* Logo */}
                <div className="grid md:grid-cols-[auto_1fr] gap-4 items-start">
                    <div className="w-32 h-24 rounded-2xl bg-shariti-black border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                        {certForm.logo_url ? (
                            <img
                                src={certForm.logo_url}
                                alt="logo"
                                className="max-w-full max-h-full object-contain"
                            />
                        ) : (
                            <ImageIcon className="w-6 h-6 text-white/30" />
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block">
                            Logo (haut du certificat)
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <label
                                className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-shariti-orange/40 text-white font-bold px-3 py-2 rounded-xl cursor-pointer transition-all text-sm"
                                data-testid="cert-upload-logo-btn"
                            >
                                <Upload className="w-4 h-4" />
                                {uploadingCert === "logo" ? "Upload..." : "Uploader"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => uploadCertImage(e.target.files?.[0], "logo")}
                                />
                            </label>
                            {certForm.logo_url && (
                                <button
                                    onClick={() => setCertForm((f) => ({ ...f, logo_url: "" }))}
                                    className="inline-flex items-center gap-1 bg-white/5 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-400 font-bold px-3 py-2 rounded-xl text-sm transition-all"
                                >
                                    <X className="w-3.5 h-3.5" /> Retirer
                                </button>
                            )}
                            <label className="inline-flex items-center gap-2 text-xs text-white/60 cursor-pointer ml-auto">
                                <input
                                    type="checkbox"
                                    checked={certForm.show_logo}
                                    onChange={(e) =>
                                        setCertForm({ ...certForm, show_logo: e.target.checked })
                                    }
                                    data-testid="cert-show-logo"
                                    className="accent-shariti-orange w-4 h-4"
                                />
                                Afficher le logo
                            </label>
                        </div>
                        <input
                            type="text"
                            placeholder="URL du logo"
                            value={certForm.logo_url}
                            onChange={(e) => setCertForm({ ...certForm, logo_url: e.target.value })}
                            data-testid="cert-logo-url"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/30 outline-none focus:border-shariti-orange/40 text-sm"
                        />
                    </div>
                </div>

                {/* Signatory */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Nom du signataire
                        </label>
                        <input
                            type="text"
                            placeholder="Bertin Tamegnon"
                            value={certForm.signatory_name}
                            onChange={(e) =>
                                setCertForm({ ...certForm, signatory_name: e.target.value })
                            }
                            data-testid="cert-signatory-name"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                            Titre du signataire
                        </label>
                        <input
                            type="text"
                            placeholder="CEO Shariti"
                            value={certForm.signatory_title}
                            onChange={(e) =>
                                setCertForm({ ...certForm, signatory_title: e.target.value })
                            }
                            data-testid="cert-signatory-title"
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                        />
                    </div>
                </div>

                {/* Signature mode picker (iter-27.6) */}
                <div className="space-y-3">
                    <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block">
                        Type de signature
                    </label>
                    <div className="grid sm:grid-cols-2 gap-2" data-testid="cert-signature-mode">
                        {[
                            {
                                value: "electronic",
                                label: "Signature électronique",
                                hint: "Le nom est généré en typo cursive directement sur le PDF — pas besoin d'image.",
                            },
                            {
                                value: "image",
                                label: "Image manuscrite (PNG)",
                                hint: "Tu uploades une signature scannée ; elle s'affiche sur le PDF.",
                            },
                        ].map((opt) => {
                            const active = certForm.signature_mode === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                        setCertForm((f) => ({
                                            ...f,
                                            signature_mode: opt.value,
                                        }))
                                    }
                                    data-testid={`cert-signature-mode-${opt.value}`}
                                    className={`text-left p-3 rounded-2xl border transition-all ${
                                        active
                                            ? "border-shariti-orange bg-shariti-orange/10"
                                            : "border-white/10 bg-white/[0.02] hover:border-white/30"
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className={`w-3.5 h-3.5 rounded-full border-2 ${
                                                active
                                                    ? "border-shariti-orange bg-shariti-orange"
                                                    : "border-white/30"
                                            }`}
                                        />
                                        <span className="text-sm font-bold text-white">
                                            {opt.label}
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-white/55 mt-1.5 leading-snug">
                                        {opt.hint}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Electronic signature live preview */}
                {certForm.signature_mode === "electronic" && (
                    <div
                        className="rounded-2xl border border-shariti-orange/20 bg-shariti-black/40 p-4"
                        data-testid="cert-electronic-preview"
                    >
                        <div className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-2">
                            Aperçu de la signature électronique
                        </div>
                        <div className="flex flex-col items-center justify-center py-3">
                            <span
                                className="text-shariti-orange-light"
                                style={{
                                    fontFamily: "'Caveat', cursive",
                                    fontSize: "44px",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                                data-testid="cert-electronic-preview-name"
                            >
                                {certForm.signatory_name || "Votre nom"}
                            </span>
                            <div className="w-48 h-px bg-shariti-orange/60 mt-1" />
                            <span className="text-[11px] text-white/65 mt-1.5 font-bold">
                                {certForm.signatory_name || "—"}
                            </span>
                            <span className="text-[10px] text-shariti-orange-light/80">
                                {certForm.signatory_title || ""}
                            </span>
                        </div>
                        <p className="text-[11px] text-white/45 leading-snug">
                            Le nom du signataire est tracé en typographie cursive
                            directement sur le PDF — légalement reconnu comme une
                            signature électronique simple.
                        </p>
                    </div>
                )}

                {/* Signature image (only in image mode) */}
                {certForm.signature_mode === "image" && (
                    <div className="grid md:grid-cols-[auto_1fr] gap-4 items-start">
                        <div className="w-32 h-16 rounded-2xl bg-shariti-black border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                            {certForm.signature_image_url ? (
                                <img
                                    src={certForm.signature_image_url}
                                    alt="signature"
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <span className="text-[10px] text-white/30 italic">aucune</span>
                            )}
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-thin tracking-[0.2em] uppercase text-white/50 block">
                                Image PNG manuscrite
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <label
                                    className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-shariti-orange/40 text-white font-bold px-3 py-2 rounded-xl cursor-pointer transition-all text-sm"
                                    data-testid="cert-upload-signature-btn"
                                >
                                    <Upload className="w-4 h-4" />
                                    {uploadingCert === "signature" ? "Upload..." : "Uploader"}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) =>
                                            uploadCertImage(e.target.files?.[0], "signature")
                                        }
                                    />
                                </label>
                                {certForm.signature_image_url && (
                                    <button
                                        onClick={() =>
                                            setCertForm((f) => ({ ...f, signature_image_url: "" }))
                                        }
                                        className="inline-flex items-center gap-1 bg-white/5 border border-white/10 hover:border-red-500/40 text-white/60 hover:text-red-400 font-bold px-3 py-2 rounded-xl text-sm transition-all"
                                    >
                                        <X className="w-3.5 h-3.5" /> Retirer
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Show signature toggle (always present) */}
                <label className="inline-flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={certForm.show_signature}
                        onChange={(e) =>
                            setCertForm({
                                ...certForm,
                                show_signature: e.target.checked,
                            })
                        }
                        data-testid="cert-show-signature"
                        className="accent-shariti-orange w-4 h-4"
                    />
                    Afficher la signature sur le certificat
                </label>

                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={saveCert}
                        disabled={savingCert}
                        data-testid="cert-settings-save"
                        className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" />
                        {savingCert ? "Sauvegarde..." : "Enregistrer le certificat"}
                    </button>
                    <button
                        onClick={downloadPreview}
                        data-testid="cert-preview-download"
                        className="inline-flex items-center gap-2 glass-card border border-shariti-orange/30 text-white font-extrabold py-3 px-5 rounded-2xl hover:border-shariti-orange transition-all"
                        title="Génère un PDF de démo avec les paramètres actuels"
                    >
                        <Award className="w-4 h-4" />
                        Aperçu PDF
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
